import { useEffect, useRef, useState } from "react";
import { Button } from "@volca/ui/components/ui/button";
import { SimpleSidebarHeader } from "@volca/ui/components/ui/simple-sidebar";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import { formatDate } from "@/core/utils/date";
import { PayrollNoteActions } from "./note-actions";
import { PayrollNoteHistory } from "./note-history";
import { PayrollNoteRow } from "./note-row";
import { usePayPeriodNotes } from "./query";

export const PayrollPeriodNotes = ({
  businessId,
  periodStart,
  locked,
  open,
  onClose,
}: {
  businessId: string | null;
  periodStart: string;
  locked: boolean;
  open: boolean;
  onClose: () => void;
}) => {
  const [historyOpen, setHistoryOpen] = useState(false);
  const query = usePayPeriodNotes(businessId, periodStart, { poll: open });
  const openNotes = query.data?.openNotes ?? [];
  const nextPeriod = query.data?.nextPeriod;
  const panelRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (open) panelRef.current?.focus();
  }, [open]);

  const close = () => {
    onClose();
    document.getElementById("payroll-notes-trigger")?.focus();
  };

  return (
    <Stack
      gap={0}
      ref={panelRef}
      hidden={!open}
      tabIndex={-1}
      onKeyDown={(event) => {
        if (event.key !== "Escape") return;
        event.stopPropagation();
        close();
      }}
      className="h-full min-h-0 min-w-0"
      render={<section id="payroll-notes-panel" aria-label="Payroll notes" />}
    >
      <SimpleSidebarHeader
        title="Payroll notes"
        description={`Period starting ${formatDate(periodStart)}`}
        onClose={close}
      />
      <Stack gap={4} className="min-h-0 flex-1 overflow-y-auto px-5 pb-5">
        <Text variant="label" aria-live="polite">
          {query.isPending
            ? "Loading…"
            : query.isError
              ? "Unavailable"
              : `${openNotes.length} open`}
        </Text>
        {query.isError ? (
          <Stack direction="row" align="center" gap={2}>
            <Text tone="destructive" role="alert">
              Payroll notes could not be loaded.
            </Text>
            <Button
              size="xs"
              variant="outline"
              onClick={() => {
                void query.refetch();
              }}
            >
              Retry
            </Button>
          </Stack>
        ) : (
          <>
            {!query.isPending && openNotes.length === 0 && (
              <Text tone="muted">No open notes for this period.</Text>
            )}
            <Stack gap={3}>
              {openNotes.map((note) => (
                <PayrollNoteRow key={note.id} note={note}>
                  {!locked && (
                    <PayrollNoteActions
                      businessId={businessId}
                      periodStart={periodStart}
                      note={note}
                      canForward={!!nextPeriod}
                    />
                  )}
                </PayrollNoteRow>
              ))}
            </Stack>
            {!query.isPending && !nextPeriod && (
              <Text variant="caption" tone="muted">
                The next open period does not match the payroll schedule. Review
                the payroll schedule before sending notes.
              </Text>
            )}
            {query.data && (
              <PayrollNoteHistory
                businessId={businessId}
                periodStart={periodStart}
                locked={locked}
                reviewedNotes={query.data.reviewedNotes}
                sentNotes={query.data.sentNotes}
                open={historyOpen}
                onOpenChange={setHistoryOpen}
              />
            )}
          </>
        )}
      </Stack>
    </Stack>
  );
};
